import React, { useState, useMemo } from 'react';
import DataTableV3 from 'src/components/DataTableV3';
import { CountyWithMunicipalities } from 'src/features/one-way-in/hooks/use-geonorge';
import { countyColumns } from '../constants/county-table-columns';
import CountyMunicipalities from './CountyMunicipalities';
import { Button } from '@innovationnorway/cl-react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { RowSelectionState } from '@tanstack/react-table';
import { TargetData } from '../hooks/subforms/use-target-form';

type Props = {
  countiesWithMunicipalities: CountyWithMunicipalities[];
  setValue: UseFormSetValue<TargetData>;
  getValues: UseFormGetValues<TargetData>;
  onClose: () => void;
};

const SelectCountyTable: React.FC<Props> = ({ countiesWithMunicipalities, setValue, getValues, onClose }) => {
  const [selectedCounties, setSelectedCounties] = useState<CountyWithMunicipalities[]>([]);

  React.useEffect(() => {
    const newCounties = selectedCounties.map((county) => county.fylkesnavn);
    const previousCounties = getValues('counties') || [];

    const removedCounties = previousCounties.filter((county) => !newCounties.includes(county));

    setValue('counties', newCounties);

    const currentMunicipalities = getValues('municipalities') || [];

    const selectedCountiesMunicipalities = selectedCounties.flatMap((county) =>
      county.kommuner.map((municipality) => municipality.kommunenummer),
    );

    const allCountyMunicipalities = new Map();
    countiesWithMunicipalities.forEach((county) => {
      county.kommuner.forEach((municipality) => {
        allCountyMunicipalities.set(municipality.kommunenummer, county.fylkesnavn);
      });
    });

    const municipalitiesToRemove = new Set();
    if (removedCounties.length > 0) {
      countiesWithMunicipalities.forEach((county) => {
        if (removedCounties.includes(county.fylkesnavn)) {
          county.kommuner.forEach((municipality) => {
            municipalitiesToRemove.add(municipality.kommunenummer);
          });
        }
      });
    }

    const updatedMunicipalities = [
      ...selectedCountiesMunicipalities,
      ...currentMunicipalities.filter((municipalityId) => {
        if (municipalitiesToRemove.has(municipalityId)) {
          return false;
        }

        const countyName = allCountyMunicipalities.get(municipalityId);
        return countyName && !newCounties.includes(countyName);
      }),
    ];

    // Remove duplicates
    const uniqueMunicipalities = Array.from(new Set(updatedMunicipalities));

    setValue('municipalities', uniqueMunicipalities);
  }, [selectedCounties, setValue, getValues, countiesWithMunicipalities]);

  const preselectedRows = useMemo(() => {
    const selectedCounties = getValues('counties') || [];
    const selectedMunicipalities = new Set(getValues('municipalities') || []);
    return countiesWithMunicipalities.reduce((acc, county, index) => {
      const allMunicipalitiesSelected =
        county.kommuner.length > 0 &&
        county.kommuner.every((m) => selectedMunicipalities.has(m.kommunenummer));
      if (selectedCounties.includes(county.fylkesnavn) || allMunicipalitiesSelected) {
        acc[index] = true;
      }
      return acc;
    }, {} as RowSelectionState);
  }, [countiesWithMunicipalities, getValues]);

  return (
    <>
      <DataTableV3
        data={countiesWithMunicipalities.sort((a, b) => a.fylkesnavn.localeCompare(b.fylkesnavn, 'nb-NO'))}
        columns={countyColumns}
        preselectedRows={preselectedRows}
        disableColumnEditor
        disableExport
        hideFilterText
        id={'countiesWithMunicipalities'}
        setSelectedRows={setSelectedCounties}
        getRowCanExpand={() => true}
        renderSubComponent={({ row }) => (
          <CountyMunicipalities row={row} getValues={getValues} setValue={setValue} />
        )}
      />
      <Button onClick={onClose} className="margin-top--5">
        Lagre
      </Button>
      <Button onClick={onClose} variant="secondary" className="margin-top--5 margin-left--1">
        Avbryt
      </Button>
    </>
  );
};

export default SelectCountyTable;
